"use client"

import * as React from 'react'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { FormHeader } from '@open-mercato/ui/backend/forms'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Button } from '@open-mercato/ui/primitives/button'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { apiCall, readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import {
  DISPATCH_TRANSITIONS,
  isTerminalDispatchStatus,
  type DispatchStatus,
} from '../../../lib/lookups'
import type { SerializedDispatch, SerializedLeg } from '../../../lib/serializers'

function getDispatchStatusVariant(status: DispatchStatus): 'default' | 'secondary' | 'destructive' | 'muted' | 'outline' {
  switch (status) {
    case 'ASSIGNED': return 'secondary'
    case 'EN_ROUTE_PICKUP':
    case 'AT_PICKUP':
    case 'LOADING':
    case 'IN_TRANSIT':
    case 'AT_DELIVERY':
    case 'UNLOADING':
      return 'default'
    case 'COMPLETED': return 'outline'
    case 'CANCELLED': return 'muted'
    default: return 'secondary'
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString()
}

async function fetchDispatchDetail(id: string): Promise<SerializedDispatch> {
  return readApiResultOrThrow<SerializedDispatch>(
    `/api/road/dispatch/assignments/${encodeURIComponent(id)}`,
    undefined,
    { errorMessage: 'Failed to fetch dispatch detail.' },
  )
}

async function fetchLegDetail(id: string): Promise<SerializedLeg> {
  return readApiResultOrThrow<SerializedLeg>(
    `/api/road/legs/${encodeURIComponent(id)}`,
    undefined,
    { errorMessage: 'Failed to fetch leg detail.' },
  )
}

export default function DispatchDetailPage({ params }: { params: { id: string } }) {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: `dispatch-detail-${params.id}` })
  const [advancePending, setAdvancePending] = React.useState(false)
  const [cancelPending, setCancelPending] = React.useState(false)

  const dispatchQuery = useQuery({
    queryKey: ['dispatch', 'detail', params.id, scopeVersion],
    queryFn: () => fetchDispatchDetail(params.id),
  })

  const legId = dispatchQuery.data?.legId ?? null
  const legQuery = useQuery({
    queryKey: ['dispatch', 'leg', legId, scopeVersion],
    queryFn: () => fetchLegDetail(legId as string),
    enabled: Boolean(legId),
  })

  const refreshDetail = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dispatch', 'detail', params.id] })
    await queryClient.invalidateQueries({ queryKey: ['dispatch', 'leg'] })
  }, [params.id, queryClient])

  const patchDispatch = React.useCallback(
    async (payload: Record<string, unknown>, errorKey: string, defaultError: string): Promise<boolean> => {
      const dispatch = dispatchQuery.data
      if (!dispatch) return false
      try {
        await runMutation({
          context: { kind: 'road.dispatch', record: { id: dispatch.id } },
          mutationPayload: payload,
          operation: async () => {
            const call = await apiCall<Record<string, unknown>>(
              `/api/road/dispatch/assignments/${encodeURIComponent(dispatch.id)}`,
              {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              },
            )
            if (!call.ok) {
              let message = t(errorKey, defaultError)
              if (call.status === 409) {
                message = t(
                  'dispatch.detail.errors.versionConflict',
                  'This dispatch was updated by someone else. Reloading…',
                )
              } else if (call.result && typeof call.result === 'object') {
                const errValue = (call.result as Record<string, unknown>).error
                if (typeof errValue === 'string') message = errValue
              }
              throw new Error(message)
            }
          },
        })
        return true
      } catch (error) {
        flash(
          error instanceof Error && error.message ? error.message : t(errorKey, defaultError),
          'error',
        )
        return false
      }
    },
    [dispatchQuery.data, runMutation, t],
  )

  const handleAdvance = React.useCallback(async (nextStatus: DispatchStatus) => {
    const dispatch = dispatchQuery.data
    if (!dispatch) return
    setAdvancePending(true)
    try {
      const ok = await patchDispatch(
        { status: nextStatus, version: dispatch.version },
        'dispatch.assign.messages.updateError',
        'Failed to update dispatch.',
      )
      if (ok) {
        flash(t('dispatch.assign.messages.updateSuccess', 'Dispatch updated.'), 'success')
        await refreshDetail()
      }
    } finally {
      setAdvancePending(false)
    }
  }, [dispatchQuery.data, patchDispatch, refreshDetail, t])

  const handleCancel = React.useCallback(async () => {
    const dispatch = dispatchQuery.data
    if (!dispatch) return
    const approved = await confirm({
      title: t('dispatch.detail.confirm.cancelTitle', 'Cancel dispatch?'),
      text: t('dispatch.detail.confirm.cancelMessage', 'The dispatch will be marked as cancelled.'),
      confirmText: t('dispatch.detail.actions.cancelDispatch', 'Cancel dispatch'),
      cancelText: t('dispatch.actions.cancelEdit', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    setCancelPending(true)
    try {
      const ok = await patchDispatch(
        { status: 'CANCELLED', version: dispatch.version },
        'dispatch.assign.messages.updateError',
        'Failed to update dispatch.',
      )
      if (ok) {
        flash(t('dispatch.assign.messages.updateSuccess', 'Dispatch updated.'), 'success')
        await refreshDetail()
      }
    } finally {
      setCancelPending(false)
    }
  }, [dispatchQuery.data, confirm, patchDispatch, refreshDetail, t])

  if (dispatchQuery.isLoading && !dispatchQuery.data) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('dispatch.messages.loadingDetail', 'Loading dispatch detail…')} />
        </PageBody>
      </Page>
    )
  }

  if (dispatchQuery.isError || !dispatchQuery.data) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage
            label={t('dispatch.detail.loadErrorTitle', 'Could not load dispatch')}
            description={t('dispatch.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  const dispatch = dispatchQuery.data
  const leg = legQuery.data ?? null
  const nextStatuses = DISPATCH_TRANSITIONS[dispatch.status].filter((next) => next !== 'CANCELLED')
  const isTerminal = isTerminalDispatchStatus(dispatch.status)
  const canCancel = !isTerminal && dispatch.status !== 'AT_DELIVERY' && dispatch.status !== 'UNLOADING'
  const statusLabel = t(`dispatch.lookups.dispatchStatus.${dispatch.status}`, dispatch.status)
  const specialRequirements = dispatch.specialRequirements ?? {}

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <FormHeader
            mode="detail"
            backHref="/backend/dispatch"
            backLabel={t('dispatch.detail.backToBoard', 'Back to dispatch board')}
            entityTypeLabel={t('dispatch.detail.entityLabel', 'Dispatch')}
            title={dispatch.id.slice(0, 8)}
            statusBadge={<Badge variant={getDispatchStatusVariant(dispatch.status)}>{statusLabel}</Badge>}
            actionsContent={(
              <div className="flex flex-wrap items-center gap-2">
                {nextStatuses.map((next) => (
                  <Button
                    key={next}
                    type="button"
                    onClick={() => { void handleAdvance(next) }}
                    disabled={advancePending || cancelPending}
                  >
                    {t('dispatch.detail.actions.advanceTo', 'Advance to {{status}}', {
                      status: t(`dispatch.lookups.dispatchStatus.${next}`, next),
                    })}
                  </Button>
                ))}
                {canCancel ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => { void handleCancel() }}
                    disabled={advancePending || cancelPending}
                  >
                    {t('dispatch.detail.actions.cancelDispatch', 'Cancel dispatch')}
                  </Button>
                ) : null}
              </div>
            )}
          />

          <section className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('dispatch.detail.sections.vehicle', 'Vehicle')}</div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.vehicleId', 'Vehicle ID')}
                  </dt>
                  <dd className="font-mono text-xs">{dispatch.vehicleId}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('dispatch.detail.sections.driver', 'Driver')}</div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.driverId', 'Driver ID')}
                  </dt>
                  <dd className="font-mono text-xs">{dispatch.driverId}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('dispatch.detail.sections.trailer', 'Trailer')}</div>
              <dl className="mt-3 space-y-1.5 text-sm">
                {dispatch.trailerId ? (
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">
                      {t('dispatch.detail.fields.trailerId', 'Trailer ID')}
                    </dt>
                    <dd className="font-mono text-xs">{dispatch.trailerId}</dd>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {t('dispatch.detail.fields.noTrailer', 'No trailer assigned.')}
                  </div>
                )}
              </dl>
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold">{t('dispatch.detail.sections.timeline', 'Timeline')}</div>
            <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.assign.fields.plannedPickupAt', 'Planned pickup')}
                </dt>
                <dd>{formatDateTime(dispatch.plannedPickupAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.assign.fields.plannedDeliveryAt', 'Planned delivery')}
                </dt>
                <dd>{formatDateTime(dispatch.plannedDeliveryAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.detail.fields.actualDepartureAt', 'Actual departure')}
                </dt>
                <dd>{formatDateTime(dispatch.actualDepartureAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.detail.fields.actualPickupAt', 'Actual pickup')}
                </dt>
                <dd>{formatDateTime(dispatch.actualPickupAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.detail.fields.actualDeliveryAt', 'Actual delivery')}
                </dt>
                <dd>{formatDateTime(dispatch.actualDeliveryAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.detail.fields.completedAt', 'Completed')}
                </dt>
                <dd>{formatDateTime(dispatch.completedAt)}</dd>
              </div>
            </dl>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{t('dispatch.detail.sections.leg', 'Leg details')}</div>
              {leg ? (
                <Link
                  href={`/backend/legs?id=${encodeURIComponent(leg.id)}`}
                  className="text-xs text-primary hover:underline"
                >
                  {t('dispatch.detail.actions.viewLeg', 'View leg')}
                </Link>
              ) : null}
            </div>
            {legQuery.isLoading ? (
              <div className="mt-3 text-xs text-muted-foreground">
                {t('dispatch.messages.loadingDetail', 'Loading dispatch detail…')}
              </div>
            ) : leg ? (
              <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.originLocationId', 'Origin location')}
                  </dt>
                  <dd className="font-mono text-xs">{leg.originLocationId ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.destinationLocationId', 'Destination location')}
                  </dt>
                  <dd className="font-mono text-xs">{leg.destinationLocationId ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.requiredVehicleType', 'Required vehicle type')}
                  </dt>
                  <dd>{leg.requiredVehicleType ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.cargoId', 'Cargo ID')}
                  </dt>
                  <dd className="font-mono text-xs">{leg.cargoId ?? '—'}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.requiredCapabilities', 'Required capabilities')}
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {leg.requiredCapabilities.length > 0
                      ? leg.requiredCapabilities.map((cap) => (
                          <Badge key={cap} variant="outline">{cap}</Badge>
                        ))
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('dispatch.detail.fields.requiredQualifications', 'Required qualifications')}
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {leg.requiredQualifications.length > 0
                      ? leg.requiredQualifications.map((qual) => (
                          <Badge key={qual} variant="outline">{qual}</Badge>
                        ))
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="mt-3 text-xs text-muted-foreground">
                {t('dispatch.detail.leg.notFound', 'Leg record is not available.')}
              </div>
            )}
          </section>

          <section className="rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold">{t('dispatch.detail.sections.notes', 'Notes and requirements')}</div>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.assign.fields.dispatcherNotes', 'Dispatcher notes')}
                </dt>
                <dd className="whitespace-pre-wrap">
                  {dispatch.dispatcherNotes?.trim()
                    ? dispatch.dispatcherNotes
                    : <span className="text-xs text-muted-foreground">—</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">
                  {t('dispatch.assign.fields.specialRequirements', 'Special requirements')}
                </dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {specialRequirements.needsCrane ? (
                    <Badge variant="outline">
                      {t('dispatch.detail.requirements.needsCrane', 'Needs crane')}
                    </Badge>
                  ) : null}
                  {specialRequirements.needsForklift ? (
                    <Badge variant="outline">
                      {t('dispatch.detail.requirements.needsForklift', 'Needs forklift')}
                    </Badge>
                  ) : null}
                  {!specialRequirements.needsCrane && !specialRequirements.needsForklift ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : null}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
